/**
 * 戦闘力カスタムビュー (PDG TE)。
 *
 * チーム共有で保存した「メンバー勾選セット」を、switch (TabGroup ピル) で 1 つずつ切り替えて
 * 戦闘力サマリー (TeamTable) を表示する。サブチームの各メンバー状況と、ビュー単位 (= サブチーム)
 * の合計を把握する用途。編集 (メンバー勾選 + ビュー名) は canEdit (admin / pi_div+team_leader /
 * pi_pmot) のみ可能で、Modal 上の検索 + チェックボックスで選び全件保存 (PUT 全置換) する。
 *
 * ビューは編集権限者が追加・名前変更・削除できる (サブチームの再編に追従するため)。件数の上限は
 * API が判定し、画面は値を持たない。
 *
 * 候補メンバープールは PDG スコープ (getCombatScope==='pdg') のメンバー。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 戦闘力カスタムビュー (PDG TE のサブチーム別メンバー勾選セット) の表示・追加・編集・削除
 * @graph-connects useCombatPowerCustomViews [calls] カスタムビュー取得フック
 * @graph-connects useSaveCombatPowerCustomViews [calls] カスタムビュー保存フック
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { getCombatScope } from '@cortex/domain-product-innovation-lab-combat-power-scope';
import type {
  CombatPowerResponse,
  CombatPowerCustomView,
  CombatPowerCustomViewInput,
} from '../../../types/combat-power.js';
import {
  useCombatPowerCustomViews,
  useSaveCombatPowerCustomViews,
} from '../../../hooks/useCombatPower.js';
import { TabGroup, TabButton } from '../../ui/Tabs/index.js';
import { TeamTable } from './TeamTable.js';

interface CombatPowerCustomViewsProps {
  data: CombatPowerResponse;
  /** name(lower) → group/team。PDG 候補プール算出に使う */
  memberTeamMap: Map<string, { group: string; team: string }>;
  currentMonthIdx: number;
  /** 担当者名 (小文字) → email。担当者名を詳細ページリンクにする */
  nameToEmail?: Map<string, string>;
}

/** @graph-connects none */
export function CombatPowerCustomViews({
  data,
  memberTeamMap,
  currentMonthIdx,
  nameToEmail,
}: CombatPowerCustomViewsProps) {
  const { t } = useTranslation();
  const { data: cvData } = useCombatPowerCustomViews();
  const saveMutation = useSaveCombatPowerCustomViews();

  const views: CombatPowerCustomView[] = useMemo(() => cvData?.views ?? [], [cvData?.views]);

  const canEdit = cvData?.canEdit ?? false;

  // PDG スコープの候補メンバー (勾選プール)。combat-power の members から getCombatScope で絞る。
  const pdgCandidates = useMemo(() => {
    return data.members.filter((m) => {
      const info = memberTeamMap.get(m.name.toLowerCase());
      if (!info) return false;
      return getCombatScope({ name: m.name, group: info.group, team: info.team }) === 'pdg';
    });
  }, [data.members, memberTeamMap]);

  const [activeIdx, setActiveIdx] = useState(0);
  const activeView: CombatPowerCustomView | undefined = views[activeIdx] ?? views[0];

  // 編集 Modal の状態。editingId が null のときは新規ビューの追加
  const [editing, setEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftSelected, setDraftSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');

  const openEditor = () => {
    if (!activeView) return;
    setEditingId(activeView.id);
    setDraftName(activeView.name);
    setDraftSelected(new Set(activeView.memberNames));
    setFilter('');
    setEditing(true);
  };

  const openAdder = () => {
    setEditingId(null);
    setDraftName('');
    setDraftSelected(new Set());
    setFilter('');
    setEditing(true);
  };

  const toggleMember = (name: string) => {
    setDraftSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toInput = (v: CombatPowerCustomView): CombatPowerCustomViewInput => ({
    id: v.id,
    name: v.name,
    memberNames: v.memberNames,
  });

  const handleSave = () => {
    const existing = views.find((v) => v.id === editingId);
    const trimmedName =
      (draftName.trim() || existing?.name) ??
      t('members.combatPower.customViews.defaultName', { n: views.length + 1 });
    const edited: CombatPowerCustomViewInput = {
      id: existing?.id ?? `view-${crypto.randomUUID()}`,
      name: trimmedName,
      memberNames: [...draftSelected],
    };
    const nextViews: CombatPowerCustomViewInput[] = existing
      ? views.map((v) => (v.id === existing.id ? edited : toInput(v)))
      : [...views.map(toInput), edited];
    saveMutation.mutate(nextViews, {
      onSuccess: () => {
        setEditing(false);
        if (!existing) setActiveIdx(nextViews.length - 1);
      },
    });
  };

  const handleDelete = () => {
    if (!editingId) return;
    if (!window.confirm(t('members.combatPower.customViews.deleteConfirm', { name: draftName })))
      return;
    const nextViews = views.filter((v) => v.id !== editingId).map(toInput);
    saveMutation.mutate(nextViews, {
      onSuccess: () => {
        setEditing(false);
        setActiveIdx(0);
      },
    });
  };

  // アクティブビューに含まれるメンバー (combat-power の表示順を保つ)
  const viewMembers = useMemo(() => {
    if (!activeView) return [];
    const set = new Set(activeView.memberNames);
    return data.members.filter((m) => set.has(m.name));
  }, [activeView, data.members]);

  const filteredCandidates = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return pdgCandidates;
    return pdgCandidates.filter((m) => m.name.toLowerCase().includes(q));
  }, [pdgCandidates, filter]);

  return (
    <Card className="animate-content-reveal">
      <CardHeader
        title={t('members.combatPower.customViews.title')}
        description={t('members.combatPower.customViews.description')}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <TabGroup ariaLabel={t('members.combatPower.customViews.title')}>
            {views.map((v, i) => (
              <TabButton
                key={v.id}
                active={i === activeIdx}
                label={v.name}
                count={v.memberNames.length}
                onClick={() => {
                  setActiveIdx(i);
                }}
              />
            ))}
          </TabGroup>
          {canEdit && activeView && (
            <Button variant="secondary" size="sm" onClick={openEditor}>
              {t('members.combatPower.customViews.edit')}
            </Button>
          )}
          {canEdit && (
            <Button variant="secondary" size="sm" onClick={openAdder}>
              {t('members.combatPower.customViews.add')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!activeView ? (
          <p className="py-8 text-center text-sm text-[var(--color-text-muted)]">
            {canEdit
              ? t('members.combatPower.customViews.noViewsEditable')
              : t('members.combatPower.customViews.noViews')}
          </p>
        ) : viewMembers.length === 0 ? (
          <p className="py-8 text-center text-sm text-[var(--color-text-muted)]">
            {canEdit
              ? t('members.combatPower.customViews.emptyEditable')
              : t('members.combatPower.customViews.empty')}
          </p>
        ) : (
          <TeamTable
            teamLabel={activeView?.name ?? ''}
            members={viewMembers}
            currentMonthIdx={currentMonthIdx}
            nameToEmail={nameToEmail}
          />
        )}
        {activeView?.updatedBy && (
          <p className="mt-3 text-xs text-[var(--color-text-muted)]">
            {t('members.combatPower.customViews.lastUpdated', { by: activeView.updatedBy })}
          </p>
        )}
      </CardContent>

      {canEdit && (
        <Modal
          open={editing}
          onClose={() => {
            setEditing(false);
          }}
          title={
            editingId
              ? t('members.combatPower.customViews.editTitle')
              : t('members.combatPower.customViews.addTitle')
          }
          size="lg"
          footer={
            <div className="flex items-center justify-end gap-2">
              {editingId && (
                <Button
                  variant="danger"
                  size="sm"
                  className="mr-auto"
                  onClick={handleDelete}
                  disabled={saveMutation.isPending}
                >
                  {t('members.combatPower.customViews.delete')}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setEditing(false);
                }}
                disabled={saveMutation.isPending}
              >
                {t('members.combatPower.customViews.cancel')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                loading={saveMutation.isPending}
              >
                {t('members.combatPower.customViews.save')}
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <Input
              label={t('members.combatPower.customViews.nameLabel')}
              value={draftName}
              maxLength={40}
              onChange={(e) => {
                setDraftName(e.target.value);
              }}
              fullWidth
            />

            <div>
              <div className="flex items-center justify-between mb-2 gap-2">
                <span className="text-sm font-medium text-[var(--color-text-primary)]">
                  {t('members.combatPower.customViews.selectMembers')}
                </span>
                <span className="text-xs text-[var(--color-text-muted)]">
                  {t('members.combatPower.customViews.selectedCount', {
                    count: draftSelected.size,
                  })}
                </span>
              </div>
              <Input
                value={filter}
                placeholder={t('members.combatPower.customViews.searchPlaceholder')}
                onChange={(e) => {
                  setFilter(e.target.value);
                }}
                fullWidth
                size="sm"
              />
              <div className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-[var(--color-border)] divide-y divide-[var(--color-border)]">
                {filteredCandidates.length === 0 ? (
                  <p className="py-6 text-center text-xs text-[var(--color-text-muted)]">
                    {t('members.combatPower.customViews.noCandidates')}
                  </p>
                ) : (
                  filteredCandidates.map((m) => (
                    <label
                      key={m.name}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[var(--color-bg-hover)] transition-colors"
                    >
                      <Checkbox
                        checked={draftSelected.has(m.name)}
                        onChange={() => {
                          toggleMember(m.name);
                        }}
                      />
                      <span className="text-sm text-[var(--color-text-primary)]">{m.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            {saveMutation.isError && (
              <p className="text-xs text-[var(--color-error)]">
                {t('members.combatPower.customViews.saveError')}
              </p>
            )}
          </div>
        </Modal>
      )}
    </Card>
  );
}
